import { useRef } from "react";
import { useLayer } from "@components/common/layer/useLayer";

import { useLang } from "@hooks/useLang";
import { getChartIcon } from "@hooks/analytics/useLocationRuleGroupByRuleType";
import { ANALYTICS_CHART_TYPE } from "@resources/Constants";
import ModalManager from "@utils/ModalManager";
import _ from "lodash";
import { Order } from "@queries/tanstack/analytics-filter/AnalyticsFilterQuery";
import { HVButton, HVIcon, HVText } from "@repo/HVComponents";
import { ChartOrderLayer } from "../../order/ChartOrderLayer";
import { OrderItem } from "../../order/OrderItem";

interface OrderingButtonProps {
  order: Order[];
  disableOrder: Order[];
  onSave: (params: { order: Order[] }) => void;
  disabled?: boolean;
}
export const OrderingButton = (props: OrderingButtonProps) => {
  const { translate } = useLang();
  const changedOrder = useRef<Order[]>(props.order);
  const isEdited = () => {
    return !_.isEqual(props.order, changedOrder.current);
  };

  const { open: openLayer, close: closeLayer } = useLayer(
    <ChartOrderLayer
      items={props.order.map((orderType) => {
        const chartType = _.snakeCase(orderType).toUpperCase();
        const ChartIcon = getChartIcon(
          chartType as keyof typeof ANALYTICS_CHART_TYPE
        );
        const disabled = props.disableOrder.includes(orderType);
        return (
          <OrderItem
            key={orderType}
            title={translate(`analytics_chart_name.${chartType.toLowerCase()}`)}
            icon={ChartIcon}
            disabled={disabled}
          />
        );
      })}
      onCancel={() => {
        if (!isEdited()) {
          closeLayer();
          return;
        }

        ModalManager.confirm(translate("not_save_message"), {
          title: translate("cancel_edit"),
          labelConfirm: translate("ok"),
          labelCancel: translate("cancel"),
          onSubmit: () => {
            closeLayer();
          }
        });
      }}
      onOk={() => {
        if (!isEdited()) {
          closeLayer();
          return;
        }

        props.onSave({
          order: [...changedOrder.current]
        });
        closeLayer();
      }}
      onClose={() => {
        if (!isEdited()) {
          closeLayer();
          return;
        }

        ModalManager.confirm(translate("not_save_message"), {
          title: translate("cancel_edit"),
          labelConfirm: translate("ok"),
          labelCancel: translate("cancel"),
          onSubmit: () => {
            closeLayer();
          }
        });
      }}
      onChangeOrder={(order) => {
        changedOrder.current = order;
      }}
    />
  );

  return (
    <HVButton
      variant="Tertiary"
      onClick={() => openLayer()}
      buttonContainerProps={{
        disabled: props.disabled,
        h: 32
      }}
    >
      <HVIcon
        name="arrow-up-down"
        svgProps={{
          width: 20,
          height: 20,
          stroke: props.disabled
            ? "var(--hv-icon-normal-disabled)"
            : "var(--hv-icon-normal-primary)"
        }}
      />
      <HVText
        c={
          props.disabled
            ? "var(--hv-text-normal-disabled)"
            : "var(--hv-text-normal-primary)"
        }
        type="Body-sm"
        style={{ marginLeft: "4px" }}
      >
        {translate("ordering")}
      </HVText>
    </HVButton>
  );
};
